import React, { useEffect, useMemo, useState } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CalendarDays, TrendingUp, Users, Eye } from "lucide-react";
import { api } from "@/lib/api";

const MONTHS_ID = ["Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli", "Agustus", "September", "Oktober", "November", "Desember"];
const WEEK_ID = ["Min", "Sen", "Sel", "Rab", "Kam", "Jum", "Sab"];
const monthLabel = (p) => { const [y, m] = p.split("-"); return `${MONTHS_ID[+m - 1]} ${y}`; };
const nf = (n) => (n || 0).toLocaleString("id-ID");

export default function ReportTimeline() {
  const [data, setData] = useState(null);
  const [selMonth, setSelMonth] = useState("");

  useEffect(() => {
    api.get("/reports/timeline").then((r) => {
      setData(r.data);
      if (r.data.months?.length) setSelMonth(r.data.months[r.data.months.length - 1].period);
    }).catch(() => {});
  }, []);

  const months = data?.months || [];
  const monthAgg = months.find((m) => m.period === selMonth);

  const daysInMonth = useMemo(() => {
    const map = {};
    (data?.days || []).filter((d) => d.period.startsWith(selMonth)).forEach((d) => { map[d.period] = d; });
    return map;
  }, [data, selMonth]);

  const grid = useMemo(() => {
    if (!selMonth) return { cells: [], max: 0 };
    const [y, m] = selMonth.split("-").map(Number);
    const first = new Date(y, m - 1, 1).getDay();
    const total = new Date(y, m, 0).getDate();
    const cells = [];
    for (let i = 0; i < first; i++) cells.push(null);
    let max = 0;
    for (let d = 1; d <= total; d++) {
      const key = `${selMonth}-${String(d).padStart(2, "0")}`;
      const agg = daysInMonth[key];
      if (agg && agg.count > max) max = agg.count;
      cells.push({ day: d, key, agg });
    }
    return { cells, max };
  }, [selMonth, daysInMonth]);

  const shade = (count) => {
    if (!count) return { bg: "transparent", border: "var(--border)", color: "var(--muted-foreground)" };
    const ratio = grid.max ? count / grid.max : 0;
    const alpha = 0.25 + ratio * 0.75;
    return { bg: `rgba(170,0,0,${alpha})`, border: "transparent", color: alpha > 0.55 ? "#fff" : "#3a2a3a" };
  };

  if (!data) return <div className="py-16 text-center text-sm text-muted-foreground">Memuat timeline…</div>;
  if (months.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-border bg-white/50 py-16 text-center text-sm text-muted-foreground" data-testid="timeline-empty">
        <CalendarDays className="w-6 h-6 mx-auto mb-2 text-showty-red/50" />
        Belum ada tanggal post yang bisa dibaca. Pastikan kolom <b>Date Post</b> terisi (mis. 2026-07-18 atau 18/07/2026).
      </div>
    );
  }

  const dailyRows = (data.days || []).filter((d) => d.period.startsWith(selMonth));

  return (
    <div className="space-y-6" data-testid="report-timeline">
      {/* Monthly summary */}
      <div>
        <h4 className="font-display text-xl text-showty-navy mb-3">Ringkasan per Bulan</h4>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="timeline-months">
          {months.map((m) => (
            <button key={m.period} onClick={() => setSelMonth(m.period)}
              className={`text-left rounded-2xl border bg-white p-4 transition-colors ${selMonth === m.period ? "border-showty-red ring-1 ring-showty-red/30" : "border-border hover:border-showty-red/40"}`}
              data-testid={`timeline-month-${m.period}`}>
              <p className="text-sm font-semibold text-showty-navy">{monthLabel(m.period)}</p>
              <p className="font-display text-2xl text-showty-red mt-1">{m.count} <span className="text-sm text-muted-foreground font-sans">post</span></p>
              <div className="mt-2 space-y-0.5 text-xs text-muted-foreground">
                <p className="flex items-center gap-1"><Users className="w-3 h-3" /> Reach {nf(m.reach)}</p>
                <p className="flex items-center gap-1"><TrendingUp className="w-3 h-3" /> Eng. {nf(m.engagement)} · ER {m.avg_er}%</p>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Calendar + daily */}
      {selMonth && (
        <div className="grid lg:grid-cols-2 gap-6">
          <div className="rounded-2xl border border-border bg-white p-5" data-testid="timeline-calendar">
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-display text-xl text-showty-navy flex items-center gap-2"><CalendarDays className="w-4 h-4 text-showty-red" /> Kalender</h4>
              <Select value={selMonth} onValueChange={setSelMonth}>
                <SelectTrigger className="w-40 h-9" data-testid="timeline-month-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {months.map((m) => <SelectItem key={m.period} value={m.period}>{monthLabel(m.period)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-7 gap-1.5 text-center">
              {WEEK_ID.map((w) => <div key={w} className="text-[11px] font-semibold text-muted-foreground py-1">{w}</div>)}
              {grid.cells.map((c, i) => {
                if (!c) return <div key={`e${i}`} />;
                const st = shade(c.agg?.count);
                return (
                  <div key={c.key} title={c.agg ? `${c.agg.count} post · reach ${nf(c.agg.reach)}` : "Tidak ada post"}
                    className="aspect-square rounded-lg border flex flex-col items-center justify-center text-xs"
                    style={{ backgroundColor: st.bg, borderColor: st.border, color: st.color }}
                    data-testid={`cal-day-${c.key}`}>
                    <span className="font-medium">{c.day}</span>
                    {c.agg && <span className="text-[10px] leading-none mt-0.5">{c.agg.count}</span>}
                  </div>
                );
              })}
            </div>
            <p className="mt-3 text-[11px] text-muted-foreground">Angka kecil = jumlah post hari itu. Warna makin merah = makin banyak post.</p>
          </div>

          <div className="rounded-2xl border border-border bg-white overflow-hidden" data-testid="timeline-daily">
            <div className="px-5 py-3 border-b border-border">
              <h4 className="font-display text-xl text-showty-navy">Ringkasan Harian — {monthLabel(selMonth)}</h4>
            </div>
            <div className="overflow-x-auto max-h-[360px] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-showty-navy text-white">
                  <tr className="text-left">
                    <th className="px-3 py-2 text-xs font-medium">Tanggal</th>
                    <th className="px-3 py-2 text-xs font-medium">Post</th>
                    <th className="px-3 py-2 text-xs font-medium">Reach</th>
                    <th className="px-3 py-2 text-xs font-medium">Views</th>
                    <th className="px-3 py-2 text-xs font-medium">Eng.</th>
                    <th className="px-3 py-2 text-xs font-medium">ER</th>
                  </tr>
                </thead>
                <tbody>
                  {dailyRows.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-muted-foreground">Tidak ada post di bulan ini</td></tr>}
                  {dailyRows.map((d) => (
                    <tr key={d.period} className="border-t border-border even:bg-showty-cream/50" data-testid={`daily-${d.period}`}>
                      <td className="px-3 py-2 whitespace-nowrap font-medium text-showty-navy">{d.period.split("-")[2]} {MONTHS_ID[+selMonth.split("-")[1] - 1].slice(0, 3)}</td>
                      <td className="px-3 py-2">{d.count}</td>
                      <td className="px-3 py-2">{nf(d.reach)}</td>
                      <td className="px-3 py-2 flex items-center gap-1"><Eye className="w-3 h-3 text-muted-foreground" />{nf(d.views)}</td>
                      <td className="px-3 py-2">{nf(d.engagement)}</td>
                      <td className="px-3 py-2 font-semibold text-showty-red">{d.avg_er}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {data.undated > 0 && (
        <p className="text-xs text-muted-foreground" data-testid="timeline-undated">
          Catatan: {data.undated} dari {data.total} baris tidak punya tanggal yang bisa dibaca, jadi tidak masuk timeline.
        </p>
      )}
    </div>
  );
}
